import React from 'react';
import { Divider, NumberFormatter } from '@mantine/core';
import PropTypes from 'prop-types';
import roundHalfEven from 'round-half-even';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { ECOM_PRODUCT_PRICING_TYPE_ENUM } from '../../../config/constants';
import { roundUpToCentValue } from '../../../helpers/format';

const calculateServiceFees = (storePrice) => {
  const price = roundHalfEven(storePrice * 1.1, 0);
  return price + roundHalfEven(price / (1 - 0.029) - price, 0) - storePrice;
};

const calculatePricingAmount = (vendorPrice, pricingType, pricingAmount) => {
  if (pricingType === ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT) {
    return vendorPrice + pricingAmount;
  }
  if (pricingType === ECOM_PRODUCT_PRICING_TYPE_ENUM.PERCENTAGE_ADJUSTMENT) {
    return roundHalfEven(vendorPrice * (1 + pricingAmount / 100), 0);
  }
  return 0;
};

const StoreBundlePricingInfo = ({
  minVendorPrice,
  maxVendorPrice,
  fkEcomPricingType,
  roundingAdjustment,
  pricingAmount
}) => {
  const minBasePrice = calculatePricingAmount(
    minVendorPrice,
    fkEcomPricingType,
    pricingAmount
  );
  const maxBasePrice = calculatePricingAmount(
    maxVendorPrice,
    fkEcomPricingType,
    pricingAmount
  );

  const minServiceFees = calculateServiceFees(minBasePrice);
  const maxServiceFees = calculateServiceFees(maxBasePrice);

  const minWebstorePrice =
    roundUpToCentValue(
      (minBasePrice + minServiceFees) / 100,
      roundingAdjustment / 100
    ) * 100;
  const maxWebstorePrice =
    roundUpToCentValue(
      (maxBasePrice + maxServiceFees) / 100,
      roundingAdjustment / 100
    ) * 100;

  const minStoreProfit = minWebstorePrice - minVendorPrice - minServiceFees;
  const maxStoreProfit = maxWebstorePrice - maxVendorPrice - maxServiceFees;

  return (
    <AppStack style={{ gap: 0 }}>
      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
          Vendor price:
        </AppText>
        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={minVendorPrice / 100}
          />
          {minVendorPrice !== maxVendorPrice && (
            <>
              {' '}
              -{' '}
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={maxVendorPrice / 100}
              />
            </>
          )}
        </AppText>
      </AppFlexbox>
      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, color: '#666' }}>Pricing:</AppText>
        <AppText style={{ fontSize: 14, color: '#666' }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix={
              fkEcomPricingType?.toString() ===
              ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT
                ? '+$'
                : '+'
            }
            style={{ fontSize: 14 }}
            suffix={
              fkEcomPricingType?.toString() !==
              ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT
                ? '%'
                : ''
            }
            thousandSeparator
            value={pricingAmount}
          />
        </AppText>
      </AppFlexbox>
      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, color: '#666' }}>Base Price:</AppText>
        <AppText style={{ fontSize: 14, color: '#666' }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={minBasePrice / 100}
          />
          {minBasePrice !== maxBasePrice && (
            <>
              {' '}
              -{' '}
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={maxBasePrice / 100}
              />
            </>
          )}
        </AppText>
      </AppFlexbox>
      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, color: '#666' }}>Fees:</AppText>

        <AppText style={{ fontSize: 14, color: '#666' }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={minServiceFees / 100}
          />
          {minServiceFees !== maxServiceFees && (
            <>
              {' '}
              -{' '}
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={maxServiceFees / 100}
              />
            </>
          )}
        </AppText>
      </AppFlexbox>
      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, color: '#666' }}>Rounding:</AppText>

        <AppText style={{ fontSize: 14, color: '#666' }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={roundingAdjustment / 100}
          />
        </AppText>
      </AppFlexbox>

      <Divider style={{ margin: '4px 0px' }} />

      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
          Store price:
        </AppText>

        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={minWebstorePrice / 100}
          />
          {minWebstorePrice !== maxWebstorePrice && (
            <>
              {' '}
              -{' '}
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={maxWebstorePrice / 100}
              />
            </>
          )}
        </AppText>
      </AppFlexbox>

      <Divider style={{ margin: '4px 0px' }} />

      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, color: '#666' }}>
          Cost of goods:
        </AppText>

        <AppText style={{ fontSize: 14, color: '#666' }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={(minVendorPrice + minServiceFees) / 100}
          />
          {minStoreProfit !== maxStoreProfit && (
            <>
              {' '}
              -{' '}
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={(maxVendorPrice + maxServiceFees) / 100}
              />
            </>
          )}
        </AppText>
      </AppFlexbox>

      <AppFlexbox
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8
        }}
      >
        <AppText style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>
          Profit:
        </AppText>

        <AppText style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={minStoreProfit / 100}
          />
          {minStoreProfit !== maxStoreProfit && (
            <>
              {' '}
              -{' '}
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{ fontSize: 14 }}
                thousandSeparator
                value={maxStoreProfit / 100}
              />
            </>
          )}
        </AppText>
      </AppFlexbox>
    </AppStack>
  );
};

StoreBundlePricingInfo.propTypes = {
  fkEcomPricingType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxVendorPrice: PropTypes.number,
  minVendorPrice: PropTypes.number,
  pricingAmount: PropTypes.number,
  roundingAdjustment: PropTypes.number
};

export default StoreBundlePricingInfo;
